import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { getBlogPosts, BlogPost } from '../../utils/blogUtils';

interface BlogPostCardProps {
  post: BlogPost;
  index: number;
}

const BlogPostCard: React.FC<BlogPostCardProps> = ({ post, index }) => {
  const { ref, inView } = useInView({
    triggerOnce: true,
    threshold: 0.2,
  });

  const [avatarUrl, setAvatarUrl] = useState<string>('');

  const handleClick = () => {
    window.scrollTo(0, 0);
  };

  useEffect(() => {
    const fetchAvatar = async () => {
      if (post.author) {
        try {
          // First try to load from public directory
          const localImage = `/${post.author}.jpg`;
          const response = await fetch(localImage);
          
          if (response.ok) {
            setAvatarUrl(localImage);
          } else {
            // If local image fails, fall back to GitHub
            const githubResponse = await fetch(`https://api.github.com/users/${post.author}`);
            if (githubResponse.ok) {
              const data = await githubResponse.json();
              setAvatarUrl(data.avatar_url);
            }
          }
        } catch (error) {
          console.error('Error fetching avatar:', error);
        }
      }
    };

    fetchAvatar();
  }, [post.author]);

  return (
    <div
      ref={ref}
      className={`transform transition-all duration-500 ${
        inView ? 'animate-fade-in-up' : 'opacity-0'
      }`}
      style={{ animationDelay: `${index * 100}ms` }}
    >
      <Link
        to={`/blog/${post.id}`}
        onClick={handleClick}
        className="block bg-gray-900 rounded-lg overflow-hidden hover:transform hover:scale-105 transition-all duration-300 group h-[280px]"
      >
        <div className="p-6 h-full flex flex-col">
          <div className="flex items-center gap-2 text-sm text-gray-400 mb-3">
            <div className="flex items-center">
              {avatarUrl ? (
                <img 
                  src={avatarUrl} 
                  alt={post.author} 
                  className="w-6 h-6 rounded-full mr-2 border border-gray-700"
                />
              ) : (
                <div className="w-6 h-6 rounded-full bg-gray-700 mr-2 flex items-center justify-center">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
                  </svg>
                </div>
              )}
              <span className="font-sans">{post.author}</span>
            </div>
            <span>•</span>
            <time className="font-sans">{new Date(post.date).toLocaleDateString()}</time>
          </div>
          <h2 className="text-2xl font-bold mb-3 text-white group-hover:text-red-500 transition-colors font-sans line-clamp-2">
            {post.title}
          </h2>
          <p className="text-gray-400 mb-4 line-clamp-3 font-sans flex-grow">
            {post.preview || 'No preview available'}
          </p>
          <div className="flex items-center text-red-500 group-hover:translate-x-2 transition-transform font-sans mt-auto">
            Read More
            <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </div>
        </div>
      </Link>
    </div>
  );
};

const BlogList: React.FC = () => {
  const { ref: h2Ref, inView: h2InView } = useInView({
    triggerOnce: true,
    threshold: 0.5,
  });
  const [posts, setPosts] = React.useState<BlogPost[]>([]);
  const [loading, setLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  React.useEffect(() => {
    const fetchPosts = async () => {
      try {
        const data = await getBlogPosts();
        setPosts(data);
        setError(null);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch posts');
      } finally {
        setLoading(false);
      }
    };

    fetchPosts();
  }, []);

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="animate-pulse space-y-8">
          {[1, 2, 3].map((i) => (
            <div key={i} className="bg-gray-800 rounded-lg p-6">
              <div className="h-6 bg-gray-700 rounded w-3/4 mb-4"></div>
              <div className="h-4 bg-gray-700 rounded w-1/2 mb-4"></div>
              <div className="space-y-2">
                <div className="h-4 bg-gray-700 rounded"></div>
                <div className="h-4 bg-gray-700 rounded w-5/6"></div>
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="text-red-500 font-sans">{error}</div>
      </div>
    );
  }

  return (
    <div className="max-w-6xl mx-auto px-4 py-12">
      <div className="flex justify-between items-center mb-16">
        <h1
          ref={h2Ref}
          className={`text-6xl font-bold text-center font-sans bg-gradient-to-r from-red-500 to-red-700 bg-clip-text text-transparent ${
            h2InView ? 'animate-title' : 'opacity-0 visibility-hidden'
          }`}
        >
          DEVLOG
        </h1>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
        {posts.map((post, index) => (
          <BlogPostCard key={post.id} post={post} index={index} />
        ))}
      </div>
    </div>
  );
};

export default BlogList; 